import { MiddlewareAPI } from 'redux';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/concatMap';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/take';
import { TypedEpic } from 'redux-observable';
import { RehydrateAction, REHYDRATE } from 'redux-persist';
import core, { App, GQL } from '@gin-betting/core';
import gamblerSettings, {
    GamblerSettings,
} from '@gin-betting/gambler-settings';

import { isMobile, SidebarView } from 'app-constants';
import { addClickInterceptor, checkIsClickInterceptorAvailable } from 'utils';
import { State, Modules } from 'types';
import moduleLocator from 'moduleLocator';
import { changeSidebarView, closeSidebar, setActiveSportType } from './actions';
import {
    UISettingKey,
    CHANGE_ODD_FORMAT,
    ENABLE_ODD_HISTORY,
    DISABLE_ODD_HISTORY,
    CHANGE_ODDS_STATISTIC,
    UI_GAMBLER_SETTINGS,
} from './constants';
import { selectSidebarView } from './selectors';
import { IMapKeyValue } from './types';

type StreamModule = Modules['stream']['default'];

type StreamActions = StreamModule['actions'];
type UIActions = typeof import('./actions');

const {
    actions: { changeGamblerSetting: unknownChangeGamblerSetting },
    epics: { makeSyncGamblerSettings },
} = gamblerSettings;
const {
    services: { registerEpic, getHistory },
    viewport: {
        selectors: { makeViewportSelector },
    },
    entities: {
        selectors: { selectSports },
        constants: { GET_SPORTS_SUCCESS },
    },
    utils: { setSearchString, getBettingOptions, getSportTypeBySportTags },
} = core;

const selectIsMedium = makeViewportSelector('lessThanOrEqual', 'medium');
const changeGamblerSetting: GamblerSettings.MakeChangeGamblerSetting<IMapKeyValue> = unknownChangeGamblerSetting;

type ChangeGamblerSettingsFn = typeof changeGamblerSetting;
type ChangeSidebarView = ReturnType<typeof changeSidebarView>;
type ChangeOddFormat = ReturnType<UIActions['changeOddFormat']>;
type EnableOddHistory = ReturnType<UIActions['enableOddHistory']>;
type DisableOddHistory = ReturnType<UIActions['disableOddHistory']>;
type ChangeOddsStatisticView = ReturnType<UIActions['changeOddsStatisticView']>;
type CloseSidebar = ReturnType<UIActions['closeSidebar']>;

moduleLocator.get('betslip').subscribe(betslip => {
    const {
        constants: { ADD_BETSLIP_ODD, DELETE_BETSLIP_ODDS },
        selectors: { selectBetslipOdds },
    } = betslip;

    type BetslipActions = typeof betslip['actions'];
    type AddBetslipOdd = ReturnType<BetslipActions['addBetslipOdd']>;
    type DeleteBetslipOdds = ReturnType<BetslipActions['deleteBetslipOdds']>;

    const setBetslipView: TypedEpic<AddBetslipOdd, ChangeSidebarView> = (
        action$,
        store
    ) =>
        action$
            .ofType(ADD_BETSLIP_ODD)
            .filter(() => !checkEqualSidebarView(SidebarView.betslip, store))
            .map(() => changeSidebarView({ sidebarView: SidebarView.betslip }));

    const setBetslipViewByRehydrate: TypedEpic<
        RehydrateAction,
        ChangeSidebarView
    > = (action$, store) =>
        action$
            .ofType(REHYDRATE)
            .filter(() => !checkEqualSidebarView(SidebarView.betslip, store))
            .filter(() => selectBetslipOdds(store.getState()).count() > 0)
            .map(() => changeSidebarView({ sidebarView: SidebarView.betslip }));

    const closeSidebarByDeleteOdds: TypedEpic<
        DeleteBetslipOdds,
        CloseSidebar
    > = action$ =>
        action$.ofType(DELETE_BETSLIP_ODDS).map(() => closeSidebar());

    const extraEpics: TypedEpic[] = [setBetslipView, setBetslipViewByRehydrate];

    if (isMobile) {
        extraEpics.push(closeSidebarByDeleteOdds);
    }

    extraEpics.forEach(epic => registerEpic(epic));
});

const makeSetBetslipByStreamOpen = (
    streamModule: StreamModule
): TypedEpic<
    ReturnType<StreamActions['openFullScreenStream']>,
    ReturnType<typeof changeSidebarView>
> => {
    const {
        constants: { OPEN_FULLSCREEN_STREAM },
    } = streamModule;

    return (action$, store) =>
        action$
            .ofType(OPEN_FULLSCREEN_STREAM)
            .filter(() => checkBreakpoint(store))
            .map(() => changeSidebarView({ sidebarView: SidebarView.live }));
};

const makeSetBetslipByStreamClosed = (
    streamModule: StreamModule
): TypedEpic<
    ReturnType<StreamActions['closeFullScreenStream']>,
    ReturnType<typeof changeSidebarView>
> => {
    const {
        constants: { CLOSE_FULLSCREEN_STREAM },
    } = streamModule;

    return (action$, store) =>
        action$
            .ofType(CLOSE_FULLSCREEN_STREAM)
            .filter(
                () =>
                    !checkEqualSidebarView(SidebarView.betslip, store) &&
                    checkIsBetslip(store) &&
                    checkBreakpoint(store)
            )
            .map(() =>
                changeSidebarView({
                    sidebarView: isMobile
                        ? SidebarView.betslip
                        : SidebarView.promo,
                })
            );
};

moduleLocator.get('stream').subscribe(streamModule => {
    const setBetslipByStreamOpen = makeSetBetslipByStreamOpen(streamModule);
    const setBetslipByStreamClosed = makeSetBetslipByStreamClosed(streamModule);

    const extraEpics: TypedEpic[] = [
        setBetslipByStreamOpen,
        setBetslipByStreamClosed,
    ];
    extraEpics.forEach(epic => registerEpic(epic));
});

export const syncEnableOddHistoryGamblerSettings: TypedEpic<
    EnableOddHistory,
    ReturnType<ChangeGamblerSettingsFn>
> = actions$ => {
    return actions$
        .ofType(ENABLE_ODD_HISTORY)
        .map(() => changeGamblerSetting(UISettingKey.SHOW_ODD_HISTORY, true));
};

export const syncDisableOddHistoryGamblerSettings: TypedEpic<
    DisableOddHistory,
    ReturnType<ChangeGamblerSettingsFn>
> = actions$ => {
    return actions$
        .ofType(DISABLE_ODD_HISTORY)
        .map(() => changeGamblerSetting(UISettingKey.SHOW_ODD_HISTORY, false));
};

export const syncOddStatisticGamblerSettings: TypedEpic<
    ChangeOddsStatisticView,
    ReturnType<ChangeGamblerSettingsFn>
> = actions$ => {
    return actions$
        .ofType(CHANGE_ODDS_STATISTIC)
        .map(action => {
            const { show, statisticView } = action.payload;
            const generatedActions: Array<
                ReturnType<ChangeGamblerSettingsFn>
            > = [];

            if (show !== undefined) {
                generatedActions.push(
                    changeGamblerSetting(UISettingKey.SHOW_ODDS_STATISTIC, show)
                );
            }

            if (statisticView !== undefined) {
                generatedActions.push(
                    changeGamblerSetting(
                        UISettingKey.ODD_STATISTIC_VIEW,
                        statisticView
                    )
                );
            }

            return generatedActions;
        })
        .concatMap(action => action);
};

export const syncOddFormatGamblerSettings: TypedEpic<
    ChangeOddFormat,
    ReturnType<ChangeGamblerSettingsFn>
> = actions$ => {
    return actions$
        .ofType(CHANGE_ODD_FORMAT)
        .map(action =>
            changeGamblerSetting(
                UISettingKey.ODD_FORMAT,
                action.payload.oddFormat
            )
        );
};

function checkIsBetslip(store: MiddlewareAPI<State>) {
    const state = store.getState();
    const currSidebarView = selectSidebarView(state);

    return currSidebarView !== SidebarView.betslip;
}

function checkBreakpoint(store: MiddlewareAPI<State>) {
    const state = store.getState();

    const isMedium = selectIsMedium(state, {});

    return !isMedium;
}

function checkEqualSidebarView(
    sidebarView: SidebarView,
    store: MiddlewareAPI<State>
) {
    const state = store.getState();
    const currSidebarView = selectSidebarView(state);

    return sidebarView === currSidebarView;
}

export const addClickInterceptorEpic: TypedEpic<
    App.ApiResponseAction<typeof GET_SPORTS_SUCCESS, GQL.GetSportsQuery>
> = (actions$, store) => {
    return actions$
        .ofType(GET_SPORTS_SUCCESS)
        .take(1)
        .switchMap(() => {
            const sports = selectSports(store.getState());

            const isClickInterceptorAvailable = checkIsClickInterceptorAvailable(
                sports
            );

            if (isClickInterceptorAvailable) {
                addClickInterceptor(() => {
                    const history = getHistory();
                    const {
                        location: { pathname },
                    } = history;
                    setSearchString({});
                    window.scroll(0, 0);

                    if (pathname !== App.Routes.UPCOMING_LIST) {
                        history.push(App.Routes.UPCOMING_LIST);
                    }
                });
            }

            return Observable.empty();
        });
};

export const setActiveSportTypeEpic: TypedEpic<
    App.ApiResponseAction<typeof GET_SPORTS_SUCCESS>,
    ReturnType<typeof setActiveSportType>
> = (actions$, store) => {
    return actions$
        .ofType(GET_SPORTS_SUCCESS)
        .take(1)
        .map(() => {
            const sports = selectSports(store.getState());
            const { selectedSportIds } = getBettingOptions();

            const sportType = getSportTypeBySportTags(sports, selectedSportIds);

            return setActiveSportType(sportType);
        });
};

export const syncGamblerSettings = makeSyncGamblerSettings(UI_GAMBLER_SETTINGS);
